class ProductVariantSwatches extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('.product-variant-swatches__list');
    this.arrows = [...this.querySelectorAll('[data-direction]')];
    if (!this.list) return;
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.imageWrap = this.closest('.grid-product')?.querySelector('.grid-product__image-wrap');
    this.list.querySelectorAll('.product-variant-swatches__item').forEach(swatch => {
      swatch.addEventListener('mouseenter', () => {
        this.hoveredSwatch = swatch;
        this.previewSwatch(this.hoveredSwatch || this.focusedSwatch);
      }, options);
      swatch.addEventListener('mouseleave', () => {
        this.hoveredSwatch = null;
        this.previewSwatch(this.focusedSwatch);
      }, options);
      swatch.addEventListener('focus', () => {
        this.focusedSwatch = swatch;
        this.previewSwatch(swatch);
      }, options);
      swatch.addEventListener('blur', () => {
        this.focusedSwatch = null;
        this.previewSwatch(this.hoveredSwatch);
      }, options);
    });
    this.arrows.forEach(arrow => {
      arrow.hidden = this.list.children.length <= 4;
      arrow.addEventListener('click', () => {
        this.list.scrollBy({
          left: Number(arrow.dataset.direction) * this.list.clientWidth,
          behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
        });
      }, options);
    });
    this.list.addEventListener('scroll', () => this.updateArrows(), options);
    this.observer = new ResizeObserver(() => this.updateArrows());
    this.observer.observe(this.list);
    this.updateArrows();
  }

  updateArrows() {
    const remaining = this.list.scrollWidth - this.list.clientWidth - this.list.scrollLeft;
    this.arrows[0].disabled = this.list.scrollLeft <= 1;
    this.arrows[1].disabled = remaining <= 1;
  }

  previewSwatch(swatch) {
    this.activeSwatch = swatch;
    if (this.preview) this.preview.hidden = true;
    if (!swatch?.dataset.variantImage || !this.imageWrap) return;
    if (!this.preview) {
      this.preview = document.createElement('div');
      this.preview.className = 'product-variant-swatches__preview';
      this.preview.setAttribute('aria-hidden', 'true');
      this.preview.hidden = true;
      this.imageWrap.append(this.preview);
    }
    // Fetch the larger image only on interaction, leaving the tile's original
    // image and its lazy-loading attributes untouched.
    const image = new Image();
    image.onload = () => {
      if (!this.isConnected || this.activeSwatch !== swatch) return;
      this.preview.style.backgroundImage = `url(${JSON.stringify(image.src)})`;
      this.preview.hidden = false;
    };
    image.src = swatch.dataset.variantImage;
  }

  disconnectedCallback() {
    this.activeSwatch = null;
    this.hoveredSwatch = null;
    this.focusedSwatch = null;
    this.preview?.remove();
    this.preview = null;
    this.controller?.abort();
    this.observer?.disconnect();
  }
}
customElements.define('product-variant-swatches', ProductVariantSwatches);

// Match empty swatch spaces to the actual visual rows, including after filtering
// or changing the number of columns at a responsive breakpoint.
(() => {
  let frame;
  const observedCards = new Set();
  const schedule = () => {
    if (frame) return;
    frame = requestAnimationFrame(updateRows);
  };
  const resizeObserver = new ResizeObserver(schedule);

  function updateRows() {
    frame = null;
    const grids = new Map();
    const currentCards = new Set();

    document.querySelectorAll('.product-variant-swatches').forEach(swatches => {
      const card = swatches.closest('.grid-product');
      if (!card || !card.getClientRects().length) return;
      currentCards.add(card);
      if (!observedCards.has(card)) {
        observedCards.add(card);
        resizeObserver.observe(card);
      }
      const grid = card.parentElement;
      if (!grids.has(grid)) grids.set(grid, []);
      const rows = grids.get(grid);
      const top = card.getBoundingClientRect().top;
      let row = rows.find(candidate => Math.abs(candidate.top - top) < 2);
      if (!row) {
        row = { top, hasImages: false, placeholders: [] };
        rows.push(row);
      }
      if (swatches.classList.contains('product-variant-swatches--empty')) {
        row.placeholders.push(swatches);
      } else if (swatches.querySelector('.product-variant-swatches__item')) {
        row.hasImages = true;
      }
    });

    // Finish measuring all rows before changing their heights.
    grids.forEach(rows => rows.forEach(row => {
      row.placeholders.forEach(placeholder => {
        placeholder.hidden = !row.hasImages;
      });
    }));
    observedCards.forEach(card => {
      if (!currentCards.has(card)) {
        resizeObserver.unobserve(card);
        observedCards.delete(card);
      }
    });
  }

  new MutationObserver(schedule).observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class', 'style', 'data-view']
  });
  window.addEventListener('resize', schedule, { passive: true });
  schedule();
})();
